import { ChevronLeft, ChevronRight } from "lucide-react";

interface Props {
  page: number; // 1-based
  pageSize: number;
  total: number;
  onPage: (p: number) => void;
  className?: string;
}

// Build a compact page-number window with ellipses, e.g. 1 … 4 5 [6] 7 8 … 20.
const pageWindow = (page: number, pages: number): (number | "…")[] => {
  if (pages <= 7) return Array.from({ length: pages }, (_, i) => i + 1);
  const out: (number | "…")[] = [1];
  const start = Math.max(2, page - 1);
  const end = Math.min(pages - 1, page + 1);
  if (start > 2) out.push("…");
  for (let i = start; i <= end; i++) out.push(i);
  if (end < pages - 1) out.push("…");
  out.push(pages);
  return out;
};

const Pagination = ({ page, pageSize, total, onPage, className = "" }: Props) => {
  const pages = Math.ceil(total / pageSize);
  if (pages <= 1) return null;

  const start = (page - 1) * pageSize + 1;
  const end = Math.min(page * pageSize, total);
  const go = (p: number) => onPage(Math.min(pages, Math.max(1, p)));

  const btn =
    "inline-flex items-center justify-center h-8 min-w-8 px-2 rounded-lg text-xs font-medium transition-all disabled:opacity-40 disabled:cursor-not-allowed";

  return (
    <div
      className={`flex items-center justify-between gap-3 flex-wrap ${className}`}
    >
      <span className="text-xs text-ink-500 tabular-nums">
        Showing {start}–{end} of {total}
      </span>
      <div className="flex items-center gap-1">
        <button
          type="button"
          onClick={() => go(page - 1)}
          disabled={page <= 1}
          className={`${btn} bg-white border border-ink-200 text-ink-600 hover:text-ink-900 hover:border-ink-300`}
          aria-label="Previous page"
        >
          <ChevronLeft size={14} />
        </button>
        {pageWindow(page, pages).map((p, i) =>
          p === "…" ? (
            <span key={`e${i}`} className="px-1 text-xs text-ink-400">
              …
            </span>
          ) : (
            <button
              key={p}
              type="button"
              onClick={() => go(p)}
              className={`${btn} ${
                p === page
                  ? "bg-ink-900 text-white shadow-sm"
                  : "bg-white border border-ink-200 text-ink-600 hover:text-ink-900 hover:border-ink-300"
              }`}
            >
              {p}
            </button>
          ),
        )}
        <button
          type="button"
          onClick={() => go(page + 1)}
          disabled={page >= pages}
          className={`${btn} bg-white border border-ink-200 text-ink-600 hover:text-ink-900 hover:border-ink-300`}
          aria-label="Next page"
        >
          <ChevronRight size={14} />
        </button>
      </div>
    </div>
  );
};

export default Pagination;
